import { useCallback, useMemo } from 'react';
import { useFittingResolver } from './assemblySlots';
import { usePartModelJobs } from './partModels';
import { useAssemblySteps } from './categoryBands';
import { useCategories } from './catalogCategories';
import { matchingRows, touchRows, useHardwareInteractions, type HardwareInteraction } from './hardwareInteractionsRun';
import { namedIn } from './partsCatalogue';
import { isHardwareOrTool, isLeftRight, sidedName, sideLabel, usePartsRun, type PartItem, type Side } from './partsRun';
import { drillGroupParts, jointKey, jointRows, stepOfLabel } from './stepHolesPrompt';
import { drillingGroups, holesOfGroup, rowHoles, touchOf, useStepHoles, type ByHoles, type MeshOf } from './stepHolesRun';
import { pieceContents, useStepsRun, type SubStep, subStepLabel } from './stepsRun';
import type { StepBand } from './categoryBands';
import { useLanguage } from '../../contexts/LanguageContext';
import { holeNumber } from './stepHoles';

// FIND HOLES' GROUPS, as the run menu lists them and the joint view tabs them: one per hardware
// table row in a step (see drillingGroups). One list, so the menu that runs a group and the tab
// that shows what it found cannot disagree about what a group is.
export type HoleGroup = {
  // The step's index, and the group's place among the step's groups.
  i: number;
  g: number;
  group: SubStep[];
  // "1AC, 1AE, …"
  label: string;
  // "Side rail (C) + Seat (D) ← Chipboard screw (H)"
  what: string;
  // AND ITS COMPONENT PARTS ALONE — "Backrest and rear legs frame + Side rail": a joint's tab names
  // what it joins, the hardware is on hover (`what`).
  parts: string;
  // The component parts the group drills.
  drilled: string[];
  // THE HARDWARE TABLE'S ROWS IT IS — both entries of a mirror pair — whose holes are what the
  // group should have found (see jointCheck).
  rows: HardwareInteraction[];
};

// A PART AS A JOINT NAMES IT: its letter, and the hands of a pair the joint takes — "Side rail
// (C, Left + Right)" when both are in it, one when only one is.
// A NAME IN THE PLURAL, by rule — no parts list prints one. English: its last word ("side rails",
// "boxes", "assemblies"). French: every word before the first linking word, the noun and the
// adjectives after it agreeing ("traverses latérales", "cadres du dossier").
const pluralWord = (w: string, language: string) => {
  if (/[sxz]$/i.test(w)) return w;
  if (language === 'fr') return /(au|eu)$/i.test(w) ? `${w}x` : /al$/i.test(w) ? `${w.slice(0, -2)}aux` : `${w}s`;
  if (/(ch|sh)$/i.test(w)) return `${w}es`;
  if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`;
  return `${w}s`;
};
const FR_LINKS = new Set(['de', 'du', 'des', 'd', 'à', 'au', 'aux', 'en', 'pour', 'avec', 'sur', 'sous']);
const pluralOf = (name: string, language: string) => {
  const words = name.split(' ');
  if (language !== 'fr') return [...words.slice(0, -1), pluralWord(words[words.length - 1], language)].join(' ');
  const stop = words.findIndex((w) => FR_LINKS.has(w.toLowerCase().replace(/['’].*$/, '')));
  const upTo = stop < 0 ? words.length : stop;
  return words.map((w, i) => (i < upTo ? pluralWord(w, language) : w)).join(' ');
};

// SHORT: the name, "Left side rail" where the joint takes one hand of a pair, and "Left & right
// side rails" ("Traverses latérales gauche et droite") where it takes both — in the plural, two of
// them. Not "+": that is what joins one part to the next in the same label.
const shortPartName = (items: PartItem[], language: string, id: string, sides: Side[]) => {
  const p = items[Number(id)];
  const named = namedIn(p, language) || `#${id}`;
  if (!isLeftRight(p) || !sides.length) return named;
  if (sides.length === 1) return sidedName(named, sides[0], language);
  const both = `${sideLabel('left', language)} ${language === 'fr' ? 'et' : '&'} ${sideLabel('right', language).toLowerCase()}`;
  const many = pluralOf(named, language);
  return language === 'fr'
    ? `${many} ${both.toLowerCase()}`
    : `${both} ${many.charAt(0).toLowerCase()}${many.slice(1)}`;
};
const shortWhat = (items: PartItem[], language: string, parts: string[], sides: Side[]) =>
  // " +" HELD TO THE PART AFTER IT by a no-break space: a line may break before the "+", never after
  // it, so a "+" never ends a line.
  parts.map((id) => shortPartName(items, language, id, sides)).join(' +\u00a0');

const jointPartName = (items: PartItem[], language: string, id: string, sides: Side[]) => {
  const p = items[Number(id)];
  const hands = isLeftRight(p) && sides.length
    ? sides.map((side) => sideLabel(side, language)).join(' + ') : '';
  const tag = [p?.letter, hands].filter(Boolean).join(', ');
  const named = namedIn(p, language) || `#${id}`;
  return tag ? `${named} (${tag})` : named;
};

export function useHoleGroups(docId: string): HoleGroup[] {
  const { language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const cats = useCategories();
  const table = useHardwareInteractions(docId);
  const interactions = useMemo(() => matchingRows(table, steps, st.subSteps), [table, steps, st.subSteps]);
  return useMemo(
    () => steps.flatMap((step, i) => drillingGroups(steps, st.subSteps, items, i, interactions)
      .map((group, g) => {
        // WHAT THE GROUP DRILLS AND WHAT GOES IN — the same halves it was grouped by.
        const contents = pieceContents(steps, st.subSteps);
        const touch = touchOf(steps, i, interactions);
        const { drilled, bores } = drillGroupParts(group[0], items, contents, touch);
        // WHICH HANDS OF A MIRROR PAIR THE GROUP DRILLS — its gestures' rows, one per hand (see
        // EntrySide): "Side rail (C, Left + Right)" when both are in it, one when only one is.
        const sides = (['left', 'right'] as Side[]).filter((side) => group.some(
          (sub) => drillGroupParts(sub, items, contents, touch).row?.side === side));
        const name = (id: string) => jointPartName(items, language, id, sides);
        const labels = group.map((sub) => subStepLabel(step.num, sub.letter));
        const what = `${drilled.map(name).join(' + ')} ← ${bores.map(name).join(' + ')}`;
        // Every row joining what this group joins, at one of its sub-steps.
        const own = group.flatMap((sub) => drillGroupParts(sub, items, contents, touch).rows);
        const rows = jointRows(own, interactions, labels);
        return {
          i, g, group, label: labels.join(', '), what, drilled, rows,
          parts: shortWhat(items, language, drilled, sides),
        };
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, st.subSteps, items, cats, language, interactions],
  );
}

// WHICH 3D MODEL EACH PART IS DRAWN WITH, resolved the way Phase 7 resolves it (see
// useFittingResolver): the kept one. What Find holes shows the call and the joint view draws.
export function useMeshOf(docId: string): MeshOf {
  const { asItem } = useFittingResolver(docId);
  const { modelOf } = usePartModelJobs();
  return useCallback<MeshOf>((id) => {
    const item = asItem(id);
    return item != null ? modelOf.get(item) ?? null : null;
  }, [asItem, modelOf]);
}

// WHETHER useMeshOf CAN ANSWER YET: the models, and what resolves a part to its catalog row, are
// in. Before, every part has no model — which is not the same as having none.
export function useMeshOfLoaded(docId: string): boolean {
  const { loaded } = useFittingResolver(docId);
  const { loaded: models } = usePartModelJobs();
  return loaded && models;
}

// A JOINT OF THE COMPONENT HOLES VIEW, one of two kinds, each a tab of its own:
// - HARDWARE (`finds`): a Find holes group — what goes into holes, found, drilled and played.
// - COVERS: what covers them or is laid on a part, placed by its own call and played in its own
//   sub-steps — the buttons over a hardware joint's screws (`capped`: that joint, whose holes they
//   sit on, drawn home and still), or a hardware table row of covers laid straight on a component
//   part — a felt pad on the feet — which drills nothing.
// Listed in the manual's order of their first sub-step, so a frame's pads come after the screws
// they follow, whichever joint first showed the frame.
export type Joint = HoleGroup & { id: string; finds: boolean; capped: Joint | null };

// A JOINT'S HOLES BY NUMBER, as the Drilling table lists them: "H101 - H104" where the numbers run on,
// "H101, H103" where they do not, '' where its rows give none. A number once, whatever its letters.
export function holeIdsLabel(g: HoleGroup, pointed = false): string {
  // P, not H, for a joint whose screws make their own holes (see shownHoleId).
  const H = pointed ? 'P' : 'H';
  const nums = [...new Set(g.rows.flatMap((r) => r.holes.flatMap((h) => h.ids ?? []))
    .map(holeNumber).filter(Number.isFinite))].sort((a, b) => a - b);
  if (!nums.length) return '';
  const run = nums.every((n, i) => !i || n === nums[i - 1] + 1);
  if (run && nums.length > 1) return `${H}${nums[0]} - ${H}${nums[nums.length - 1]}`;
  return nums.map((n) => `${H}${n}`).join(', ');
}

// The sub-steps of `rows` that bring one of the rows' covers, in the step of the first, each once.
//
// BY WHAT THEY BRING, in the steps the rows name — never by the rows' own sub-step labels, which
// are the letters of the analysis the table was read against and move when Phase 4 runs again
// (see drillGroupParts). A gesture that says which hand it holds goes with that hand's row.
const coveringOf = (rows: HardwareInteraction[], steps: StepBand[], subSteps: Record<string, SubStep[]>) => {
  const found = rows.flatMap((r) => [...new Set(r.substeps.map(stepOfLabel))].flatMap((num) => {
    const i = steps.findIndex((s) => String(s.num) === num);
    if (i < 0) return [];
    return (subSteps[steps[i].category] ?? []).filter((sub) => {
      const added = (sub.parts ?? []).filter((u) => !u.role || u.role === 'added');
      const hands = new Set((sub.parts ?? []).flatMap((u) => (u.side ? [u.side] : [])));
      return added.some((u) => r.covers.includes(u.id)) && (!r.side || !hands.size || hands.has(r.side));
    }).map((sub) => ({ i, sub }));
  }));
  const i = found.length ? Math.min(...found.map((g) => g.i)) : -1;
  const group = i < 0 ? [] : (subSteps[steps[i].category] ?? []).filter((sub) => found.some((g) => g.i === i && g.sub.letter === sub.letter));
  return { i, group };
};

export function useJoints(docId: string): Joint[] {
  const { language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const table = useHardwareInteractions(docId);
  const groups = useHoleGroups(docId);
  const holes = useStepHoles(docId);
  return useMemo(() => {
    // THE STEP AND WHAT IT JOINS — stable across a re-run, where a group's place in a list is not.
    const idOf = (i: number, row: HardwareInteraction | undefined, g: number) =>
      `${i}|${row ? jointKey(row) : `#${g}`}`;
    const label = (i: number, group: SubStep[]) => group.map((sub) => subStepLabel(steps[i].num, sub.letter)).join(', ');
    // EACH ROW NAMING THE HOLES IT MAKES NOW (see rowHoles): the table's ids are Find holes' when the
    // table was read, and a re-run renumbers them — step 2's right screw row named H206, a dowel since,
    // and its screw read "not placed" while H202 stood drilled. Matched per part and per hardware.
    const all = Object.values(holes.byStep).flatMap((f) => f.holes);
    const fresh = (i: number) => (r: HardwareInteraction): HardwareInteraction => {
      const mine = rowHoles(r, all, holes.byStep[steps[i]?.category ?? '']?.holes ?? []);
      return {
        ...r,
        holes: r.holes.map((h) => {
          const ids = mine.filter((o) => o.part === h.part && o.via === h.for).map((o) => o.id)
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          return ids.length ? { ...h, ids } : h;
        }),
      };
    };
    const drilling = groups.map((g): Joint => ({
      ...g, rows: g.rows.map(fresh(g.i)), id: idOf(g.i, g.rows[0], g.g), finds: true, capped: null,
    }));
    // THE BUTTONS OVER A HARDWARE JOINT'S SCREWS: its rows that carry covers, on the parts the
    // hardware goes through (where its heads are).
    const capping = drilling.flatMap((j, k): Joint[] => {
      const rows = j.rows.filter((r) => r.covers.length);
      if (!rows.length) return [];
      const { i, group } = coveringOf(rows, steps, st.subSteps);
      const at = i < 0 ? j.i : i;
      const through = [...new Set(rows.flatMap((r) => r.through ?? []))];
      const drilled = (through.length ? through : j.drilled).filter((id) => items[Number(id)] && !isHardwareOrTool(items[Number(id)]));
      const covers = [...new Set(rows.flatMap((r) => r.covers))];
      const sides = [...new Set(rows.flatMap((r) => (r.side ? [r.side] : [])))];
      const name = (id: string) => jointPartName(items, language, id, sides);
      return [{
        i: at, g: -1000 - k, group, drilled, rows, finds: false, capped: j,
        id: `${j.id}|covers`,
        label: label(at, group),
        what: `${drilled.map(name).join(' + ')} ← ${covers.map(name).join(' + ')}`,
        parts: shortWhat(items, language, drilled, sides),
      }];
    });
    const taken = new Set(groups.flatMap((g) => g.rows.map(jointKey)));
    const byKey = new Map<string, HardwareInteraction[]>();
    for (const r of touchRows(table)) {
      const laid = r.covers.length > 0 && r.hardware.every((h) => r.covers.includes(h));
      if (!laid || !r.components.length || taken.has(jointKey(r))) continue;
      byKey.set(jointKey(r), [...(byKey.get(jointKey(r)) ?? []), r]);
    }
    const laid = [...byKey.values()].flatMap((rows, k): Joint[] => {
      const { i, group } = coveringOf(rows, steps, st.subSteps);
      if (i < 0) return [];
      const drilled = rows[0].components.filter((id) => items[Number(id)] && !isHardwareOrTool(items[Number(id)]));
      const sides = [...new Set(rows.flatMap((r) => (r.side ? [r.side] : [])))];
      const name = (id: string) => jointPartName(items, language, id, sides);
      return [{
        i, g: -1 - k, group, drilled, rows, finds: false, capped: null,
        id: idOf(i, rows[0], -1 - k),
        label: label(i, group),
        what: `${drilled.map(name).join(' + ')} ← ${rows[0].hardware.map(name).join(' + ')}`,
        parts: shortWhat(items, language, drilled, sides),
      }];
    });
    // IN THE MANUAL'S ORDER: by step, then by where the first sub-step stands in it.
    const first = (j: Joint) => (j.group[0] ? (st.subSteps[steps[j.i]?.category ?? ''] ?? []).findIndex((o) => o.letter === j.group[0].letter) : 1e6);
    return [...drilling, ...capping, ...laid].sort((a, b) => a.i - b.i || first(a) - first(b));
  }, [groups, table, steps, st.subSteps, items, language, holes.byStep]);
}

// THE HOLES A JOINT STANDS ON: a hardware joint's own, named by its rows; a covers joint's, the
// ones of the joint whose screws it caps; none for covers laid on a part.
export const holesOfJoint = (byStep: ByHoles, steps: StepBand[], j: Joint) => {
  const of = j.finds ? j : j.capped;
  if (!of) return [];
  // THE HOLES ITS ROWS NAME, BY ID: what the hardware table says this joint makes, whatever the
  // gestures say. A gesture that does not say which hand it works on matches no one row (see
  // drillGroupParts), and read through the gestures its holes were "not placed" while standing
  // drilled in the wood. Through the gestures only for a table from before its holes had ids.
  const ids = new Set(of.rows.flatMap((r) => r.holes.flatMap((h) => h.ids ?? [])));
  if (!ids.size) return holesOfGroup(byStep, steps[of.i]?.category ?? '', of.group);
  const all = Object.values(byStep).flatMap((f) => f.holes)
    .filter((h, k, every) => every.findIndex((o) => o.id === h.id) === k);
  const step = byStep[steps[of.i]?.category ?? '']?.holes ?? [];
  return of.rows.flatMap((r) => rowHoles(r, all, step))
    .filter((h, k, every) => every.findIndex((o) => o.id === h.id) === k);
};
